"use client"
import { t } from "@/languages/i18n"
import { ComponentConfig } from "@measured/puck"
import { useState } from "react"
import { twMerge } from "tailwind-merge"

export type EmailSubscriptionProps = {
  buttonText?: string
  className?: string
}

interface FormData {
  fullName: string
  email: string
  phone: string
}

interface SettingsData {
  attributes: {
    key: string
    value: string
  }
}

export const RenderConfig: ComponentConfig<EmailSubscriptionProps> = {
  render: ({ buttonText, className }) => {
    const [formData, setFormData] = useState<FormData>({
      fullName: "",
      email: "",
      phone: "",
    })
    const [isLoading, setIsLoading] = useState(false)
    const [message, setMessage] = useState("")
    const [errors, setErrors] = useState<Partial<FormData>>({})
    const [settingsData, setSettingsData] = useState<SettingsData[]>([])

    const validateForm = (): boolean => {
      const newErrors: Partial<FormData> = {}

      if (!formData.fullName.trim()) {
        newErrors.fullName = t("Please fill out this field")
      }

      if (!formData.email.trim()) {
        newErrors.email = t("Please fill out this field")
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = t("Please enter a valid email")
      }

      if (!formData.phone.trim()) {
        newErrors.phone = t("Please fill out this field")
      }

      setErrors(newErrors)
      return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()

      if (!validateForm()) {
        return
      }

      setIsLoading(true)
      setMessage("")

      try {
        const emailTo = settingsData.find((item: SettingsData) => item.attributes.key === "emailContact")?.attributes
          .value

        const response = await fetch("/api/email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: formData.email,
            toEmail: emailTo,
            subject: "New Subscription",
            content: `
              Full Name: ${formData.fullName}
              Email: ${formData.email}
              Phone: ${formData.phone}
            `,
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || "Failed to submit data")
        }

        const result = await response.json()
        setMessage(result.message || t("Registration successful!"))

        setFormData({
          fullName: "",
          email: "",
          phone: "",
        })
        setErrors({})
      } catch (error) {
        console.error("Error:", error)
        setMessage(error instanceof Error ? error.message : t("An error occurred during registration!"))
      } finally {
        setIsLoading(false)
      }
    }

    const handleInputChange = (field: keyof FormData, value: string) => {
      setFormData(prev => ({ ...prev, [field]: value }))
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: undefined }))
      }
    }

    return (
      <div className={twMerge("w-full bg-white", className)}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col lg:gap-2 sm:gap-1 gap-0">
            <label className="lg:text-base lg:leading-[30px] sm:text-[15px] text-sm text-[#07121F]">
              {t("Full Name")}
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.fullName}
                onChange={e => handleInputChange("fullName", e.target.value)}
                className={`w-full py-1 border-0 border-b-2 bg-transparent focus:outline-none focus:ring-0 ${
                  errors.fullName ? "border-red-500" : "border-gray-300 focus:border-gray-400"
                }`}
                placeholder=""
              />
            </div>
            {errors.fullName && <p className="text-red-500 text-sm">{errors.fullName}</p>}
          </div>

          <div className="flex flex-col lg:gap-2 sm:gap-1 gap-0">
            <label className="lg:text-base lg:leading-[30px] sm:text-[15px] text-sm text-[#07121F]">Email</label>
            <div className="relative">
              <input
                type="email"
                value={formData.email}
                onChange={e => handleInputChange("email", e.target.value)}
                className={`w-full py-1 border-0 border-b-2 bg-transparent focus:outline-none focus:ring-0 ${
                  errors.email ? "border-red-500" : "border-gray-300 focus:border-gray-400"
                }`}
                placeholder=""
              />
            </div>
            {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
          </div>

          <div className="flex flex-col lg:gap-2 sm:gap-1 gap-0">
            <label className="lg:text-base lg:leading-[30px] sm:text-[15px] text-sm text-[#07121F]">
              {t("Phone Number")}
            </label>
            <div className="relative">
              <input
                type="tel"
                value={formData.phone}
                onChange={e => handleInputChange("phone", e.target.value)}
                className={`w-full py-1 border-0 border-b-2 bg-transparent focus:outline-none focus:ring-0 ${
                  errors.phone ? "border-red-500" : "border-gray-300 focus:border-gray-400"
                }`}
                placeholder=""
              />
            </div>
            {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#1E4259] text-white font-semibold py-3 px-4 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed border border-transparent hover:bg-[#fff] rounded-3xl hover:text-[#1E4259] hover:border hover:border-[#1E4259] text-base"
          >
            {isLoading ? t("Sending...") : buttonText || t("Submit")}
          </button>

          {message && (
            <div
              className={`text-center text-sm ${
                message.includes("successful") || message.includes("thành công") ? "text-green-600" : "text-red-500"
              }`}
            >
              {message}
            </div>
          )}
        </form>
      </div>
    )
  },
}
